import React, { useMemo } from 'react';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import { DatePicker, TimePicker } from '../ui/DatePicker';
import { holidayName } from '../../utils/federalHolidays';

/**
 * WORangePicker — single airline-style range control (§1.1).
 * One control for start → end. End defaults same-day as start (§1.1).
 * Default times pre-populate from Settings (§1.2). Federal holidays flagged (§1.3).
 *
 * Controlled component. value = { startDate, endDate, startTime, endTime }.
 */
export function WORangePicker({ value, onChange, error }) {
  const v = value || {};
  const startHoliday = v.startDate ? holidayName(v.startDate) : null;
  const endHoliday = v.endDate && v.endDate !== v.startDate ? holidayName(v.endDate) : null;

  const set = (patch) => {
    const next = { ...v, ...patch };
    // End defaults to / clamps to same-day as start (§1.1)
    if (patch.startDate) {
      if (!next.endDate || next.endDate < patch.startDate) next.endDate = patch.startDate;
    }
    if (patch.endDate && next.startDate && patch.endDate < next.startDate) {
      next.endDate = next.startDate;
    }
    onChange(next);
  };

  const multiDay = v.startDate && v.endDate && v.endDate !== v.startDate;

  const holidayHint = useMemo(() => {
    if (startHoliday && endHoliday) return `${startHoliday} (start) · ${endHoliday} (end)`;
    return startHoliday || endHoliday;
  }, [startHoliday, endHoliday]);

  return (
    <div>
      <label className="label">
        <CalendarDaysIcon className="h-4 w-4 inline mr-1" />
        Schedule {multiDay && <span className="text-xs text-gray-400">(multi-day)</span>}
      </label>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <span className="block text-xs text-gray-500 mb-1">Start date</span>
          <DatePicker
            value={v.startDate || ''}
            onChange={(val) => set({ startDate: val })}
            error={error}
          />
        </div>
        <div>
          <span className="block text-xs text-gray-500 mb-1">Stop date</span>
          <DatePicker
            value={v.endDate || ''}
            min={v.startDate || undefined}
            onChange={(val) => set({ endDate: val })}
          />
        </div>
      </div>
      {holidayHint && (
        <p className="mt-1 text-xs text-amber-600 flex items-center gap-1">
          <span aria-hidden>🇺🇸</span> Federal holiday: {holidayHint}
        </p>
      )}
      <div className="grid grid-cols-2 gap-3 mt-2">
        <div>
          <span className="block text-xs text-gray-500 mb-1">Start time</span>
          <TimePicker
            value={v.startTime || ''}
            onChange={(val) => set({ startTime: val })}
          />
        </div>
        <div>
          <span className="block text-xs text-gray-500 mb-1">Stop time</span>
          <TimePicker
            value={v.endTime || ''}
            onChange={(val) => set({ endTime: val })}
          />
        </div>
      </div>
    </div>
  );
}

export default WORangePicker;
